import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { toast } from 'sonner'
import { keycardAPI } from '#/api/keycard'
import { useNavigate } from '#/hooks/use-navigate'


interface FormInputs {
  id: string
}

interface DeleteKeycardModalProps {
  id: string
  onClose: () => void
}

export function DeleteKeycardModal({ id, onClose }: DeleteKeycardModalProps) {
  const { t } = useTranslation([ 'keycards' ])
  const { organization } = useParams()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const form = useForm<FormInputs>({
    defaultValues: {
      id: ''
    }
  })

  const removeKeycardMutation = useMutation({
    mutationFn: () => keycardAPI.delete(id),
    onSuccess: () => {
      // Invalidate the specific keycard and the keycard list
      queryClient.invalidateQueries({ queryKey: [ 'organizations', organization, 'keycards' ] })
      queryClient.removeQueries({
        queryKey: [ 'organizations', organization, 'keycards', id ]
      })
      toast.success(t('keycard_list.menu.toasts.remove_success'))
      onClose()
      navigate('..')
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  const handleSubmit = form.handleSubmit(values => {
    if ((values.id ?? '').trim() !== id.trim()) {
      form.setError('id', {
        type: 'manual',
        message: t('keycard_list.menu.toasts.remove_mismatch')
      })

      return
    }

    removeKeycardMutation.mutate()
  })

  return (
    <Dialog open modal onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('keycard_list.menu.remove_title', { id })}</DialogTitle>
          <DialogDescription>{t('keycard_list.menu.remove_description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {removeKeycardMutation.error && (
              <AlertBox
                error
                title={mutationError(removeKeycardMutation.error, {
                  default: t('keycard_list.menu.toasts.remove_error')
                })}
              />
            )}

            <FormField
              name="id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('keycard_list.menu.remove_field')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={128}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={onClose}>
                {t('keycard_list.menu.remove_cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={removeKeycardMutation.isPending}
              >
                {t('keycard_list.menu.remove_submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
